import { COLOR } from '@nk/utils-lib';
import Feature from 'ol/Feature';
import Map from 'ol/Map.js';
import { FitOptions } from 'ol/View';
import { Control } from 'ol/control';
import { Coordinate } from 'ol/coordinate';
import { getCenter } from 'ol/extent';
import { Circle, Point, Polygon } from 'ol/geom';
import { Type } from 'ol/geom/Geometry';
import { fromCircle } from 'ol/geom/Polygon';
import { fromLonLat, getPointResolution, toLonLat } from 'ol/proj';
import { getArea, getDistance } from 'ol/sphere';
import { Fill, Icon, Stroke, Style, Text } from 'ol/style';
import { FlatStyleLike } from 'ol/style/flat';

export const DEFAULT_STARTING_POINT = fromLonLat([8.4689, 60.472]);

export interface GeoMapConfig {
  id: string;
  startingPoint: Coordinate | Coordinate[];
  height: number | '100%';
  scrollable: boolean;
  grayscale: boolean;
  zoom: number;
  controls: {
    zoom: boolean;
    addCircle: boolean;
    addPolygon: boolean;
    clearMap: boolean;
    setFocus: boolean;
  };
}

export const DEFAULT_GEO_MAP_CONFIG: GeoMapConfig = {
  startingPoint: DEFAULT_STARTING_POINT,
  id: 'geo-map',
  height: 300,
  scrollable: false,
  grayscale: false,
  zoom: 5,
  controls: {
    zoom: false,
    addCircle: false,
    addPolygon: false,
    clearMap: false,
    setFocus: false,
  },
};

export const MINI_GEO_MAP_CONFIG: GeoMapConfig = {
  startingPoint: DEFAULT_STARTING_POINT,
  id: 'mini-map',
  height: 120,
  scrollable: false,
  grayscale: false,
  zoom: 5,
  controls: {
    zoom: false,
    addCircle: false,
    addPolygon: false,
    clearMap: false,
    setFocus: false,
  },
};

export const SMALL_GEO_MAP_CONFIG: GeoMapConfig = {
  id: 'geo-map',
  height: 300,
  startingPoint: DEFAULT_STARTING_POINT,
  zoom: 18,
  scrollable: true,
  grayscale: false,
  controls: {
    zoom: true,
    addCircle: false,
    addPolygon: false,
    clearMap: false,
    setFocus: false,
  },
};

export const EDITABLE_GEO_MAP_CONFIG: GeoMapConfig = {
  startingPoint: DEFAULT_STARTING_POINT,
  id: 'editable-map',
  height: 400,
  scrollable: true,
  grayscale: false,
  zoom: 5,
  controls: {
    zoom: true,
    addCircle: true,
    addPolygon: true,
    clearMap: true,
    setFocus: true,
  },
};

export type GeoGeometryType = Extract<Type, 'Polygon' | 'Circle'>;

interface GeoGeometryPolygonGeoJson {
  type: 'Feature';
  geometry: {
    type: 'Polygon';
    /**
     * List of coordinates in the following order [Latitude, Longitude]
     */
    coordinates: Coordinate[][];
  };
}

interface GeoGeometryCircleGeoJson {
  type: 'Feature';
  properties: {
    /**
     * Radius in meters
     */
    radius: number;
  };
  geometry: {
    type: 'Point';
    /**
     * Coordinates in the following order [Latitude, Longitude]
     */
    coordinates: Coordinate;
  };
}

export type GeoGeometryGeoJson = GeoGeometryPolygonGeoJson | GeoGeometryCircleGeoJson;

export function isGeoGeometryPolygonGeoJson(item: unknown): item is GeoGeometryPolygonGeoJson {
  return (item as GeoGeometryPolygonGeoJson)?.geometry?.type === 'Polygon';
}
export function isGeoGeometryCircleGeoJson(item: unknown): item is GeoGeometryCircleGeoJson {
  return (
    (item as GeoGeometryCircleGeoJson)?.properties && (item as GeoGeometryCircleGeoJson)?.geometry?.type === 'Point'
  );
}

export const LOCATION_MAP_CUSTOM_CONTROLS_CLASS_NAME = 'browser-filter-location-map-custom-controls';
export const LOCATION_MAP_BUTTON_CONTROL_CLASS_NAME = 'location-map-button-control';
const LOCATION_MAP_BUTTON_CONTROL_SELECTED_CLASS_NAME = 'location-map-button-control-selected';

export const LOCATION_MAP_BUTTON_CONTROL_CIRCLE = 'location-map-button-control-circle';
export const LOCATION_MAP_BUTTON_CONTROL_POLYGON = 'location-map-button-control-polygon';
export const LOCATION_MAP_BUTTON_CONTROL_CLEAR = 'location-map-button-control-clear';
export const LOCATION_MAP_BUTTON_CONTROL_ZOOM = 'location-map-button-control-zoom';

export interface LocationMapCustomButtonControl {
  id: string;
  className: string;
  iconClassName: string;
  title: string;
  text: string;
  clickFn: (...args: unknown[]) => void;
}

interface LocationMapCustomControlsOptions {
  className: string;
  buttons: LocationMapCustomButtonControl[];
  target: string | HTMLElement | undefined;
}

export class LocationMapCustomControls extends Control {
  constructor(options: LocationMapCustomControlsOptions) {
    const containerElement = document.createElement('div');
    containerElement.className = `ol-unselectable ol-control ${options.className}`;

    for (const button of options.buttons) {
      const btnElement = document.createElement('button');
      btnElement.id = button.id;
      btnElement.className = button.className;
      btnElement.title = button.title;
      btnElement.innerHTML = `${button.text} <i class="${button.iconClassName} ${button.text ? 'ms-1' : ''}"></i>`;
      containerElement.appendChild(btnElement);
      btnElement.addEventListener('click', button.clickFn, false);
    }

    super({
      element: containerElement,
      target: options.target,
    });
  }
}

export function clearButtonClasses(mapId: string): void {
  document
    .querySelectorAll(
      `#${mapId} .${LOCATION_MAP_CUSTOM_CONTROLS_CLASS_NAME} .${LOCATION_MAP_BUTTON_CONTROL_CLASS_NAME}`,
    )
    .forEach((elem) => elem.classList.remove(LOCATION_MAP_BUTTON_CONTROL_SELECTED_CLASS_NAME));
}

export function setButtonVisibility(mapId: string, buttonId: string, hidden: boolean): void {
  const button = document.querySelector(`#${mapId} .${LOCATION_MAP_CUSTOM_CONTROLS_CLASS_NAME} #${buttonId}`);
  if (!button) {
    return;
  }
  if (hidden) {
    button.classList.add('hidden');
  } else {
    button.classList.remove('hidden');
  }
}

export function toggleButtonClass(mapId: string, buttonId: string): boolean | null {
  document
    .querySelectorAll(
      `#${mapId} .${LOCATION_MAP_CUSTOM_CONTROLS_CLASS_NAME} .${LOCATION_MAP_BUTTON_CONTROL_CLASS_NAME}:not(#${buttonId})`,
    )
    .forEach((elem) => elem.classList.remove(LOCATION_MAP_BUTTON_CONTROL_SELECTED_CLASS_NAME));

  const button = document.querySelector(`#${mapId} .${LOCATION_MAP_CUSTOM_CONTROLS_CLASS_NAME} #${buttonId}`);
  if (!button) {
    return null;
  }
  if (button.classList.contains(LOCATION_MAP_BUTTON_CONTROL_SELECTED_CLASS_NAME)) {
    button.classList.remove(LOCATION_MAP_BUTTON_CONTROL_SELECTED_CLASS_NAME);
    return false;
  } else {
    button.classList.add(LOCATION_MAP_BUTTON_CONTROL_SELECTED_CLASS_NAME);
    return true;
  }
}

/* eslint-disable @typescript-eslint/naming-convention*/
export const LOCATION_MAP_STYLE: FlatStyleLike = {
  'fill-color': [77, 133, 217, 0.3],
  'stroke-color': COLOR.blue,
  'stroke-width': 3,
  'circle-radius': 3,
  'circle-fill-color': COLOR.blue,
};
/* eslint-enable @typescript-eslint/naming-convention*/

export interface FormattedArea {
  value: number;
  units: 'm2' | 'km2';
}

export function formatArea(figure: Polygon | Circle): FormattedArea {
  let polygon: Polygon;
  if (figure instanceof Polygon) {
    polygon = figure;
  } else {
    polygon = fromCircle(figure);
  }
  const area = getArea(polygon, { projection: EPSG_3857_PROJECTION });
  if (area > 10000) {
    return {
      value: Math.round((area / 1000) * 100) / 100,
      units: 'km2',
    };
  } else {
    return {
      value: Math.round(area * 100) / 100,
      units: 'm2',
    };
  }
}

/**
 * @param lat - location north
 * @param lon - location east
 * @param text - label for the marker
 */
export interface MapMarker {
  /**
   * Label for the marker
   */
  text: string;
  /**
   * Location north
   */
  lat: number;
  /**
   * Location east
   */
  lon: number;
}

export function getMapMarker(config: MapMarker): Feature<Point> {
  const markerFeature = new Feature({
    geometry: new Point([config.lat, config.lon]),
    text: config.text,
  });

  const iconStyle = new Style({
    image: new Icon({
      anchor: [12, 24],
      scale: 1.5,
      anchorXUnits: 'pixels',
      anchorYUnits: 'pixels',
      src: 'app/assets/img/marker-map-icon.svg',
    }),
    text: new Text({
      text: config.text,
      font: "1rem 'Lato', sans-serif",
      textAlign: 'start',
      textBaseline: 'middle',
      fill: new Fill({ color: COLOR.red }),
      stroke: new Stroke({ color: COLOR.white, width: 3 }),
      offsetX: 16,
      offsetY: -19,
      placement: 'point',
    }),
  });

  markerFeature.setStyle(iconStyle);
  return markerFeature;
}

export function getCircleGeoJson(circle: Circle, map: Map): GeoGeometryCircleGeoJson {
  const perimeter = fromCircle(circle);
  const perimeterCoords = perimeter.getCoordinates()[0];
  const pointOnPerimeter = toLonLat(perimeterCoords[0]);

  const center = (circle.clone().transform(map.getView().getProjection(), 'EPSG:4326') as Circle).getCenter();
  const radius = getDistance(center, pointOnPerimeter);
  return {
    type: 'Feature',
    properties: {
      radius,
    },
    geometry: { type: 'Point', coordinates: center },
  };
}

export function transformRadius(radius: number, center: Coordinate, map: Map): number {
  const viewProjection = map.getView().getProjection();
  const pointResolution = getPointResolution(viewProjection, 1, center);
  return radius / pointResolution;
}

function getPolylocationStr(flatCoordinates: number[]): string {
  const latitudes = flatCoordinates.filter((i, index) => (index % 2) - 1);
  const longtitudes = flatCoordinates.filter((i, index) => index % 2);
  return latitudes
    .map((lat, index) => {
      const [longtitude, latitude] = toLonLat([lat, longtitudes[index]]);
      return `${latitude} ${longtitude}`;
    })
    .join(', ');
}

export const FIT_OPTIONS: FitOptions = { padding: [10, 10, 10, 10], minResolution: 1 };

/**
 * There are many different spatial reference systems used across the world, each with its own SRID. Some common SRIDs include:
 *
 * 4326: WGS 84 (longitude/latitude)
 * 3857: Web Mercator (meters)
 * 27700: British National Grid (meters)
 * When working with geographic data (latitude/longitude coordinates), it's common to use SRID 4326, which is the SRID for the WGS 84 reference system. When working with data that uses a projected coordinate system (meters), other SRIDs may be more appropriate, depending on the specific requirements of the project.
 */
export const DEFAULT_PROJECTION = 'EPSG:4326'; // geonorge uses EPSG:4258. Why?
/**
 * Open layers uses EPSG:3857 as a default projection
 * [See docs]{@link https://openlayers.org/doc/faq.html#what-projection-is-openlayers-using-}
 */
export const EPSG_3857_PROJECTION = 'EPSG:3857';

export function exportPng(map: Map, fileName: string): void {
  const mapCanvas = document.createElement('canvas');
  const size = map.getSize();
  if (typeof size !== 'undefined') {
    mapCanvas.width = size[0];
    mapCanvas.height = size[1];
    const mapContext = mapCanvas.getContext('2d');
    if (mapContext) {
      const layers = map.getViewport().querySelectorAll(`.ol-layers canvas, canvas.ol-layers`);
      layers.forEach((canvas) => {
        if (canvas instanceof HTMLCanvasElement && canvas.width > 0) {
          const parentNode = canvas.parentNode as ParentNode & { style: CSSStyleDeclaration };
          const opacity = parentNode.style.opacity || canvas.style.opacity;
          mapContext.globalAlpha = opacity === '' ? 1 : Number(opacity);
          let matrix;
          const transform = canvas.style.transform;
          if (transform) {
            // Get the transform parameters from the style's transform matrix
            const match = transform.match(/^matrix\(([^(]*)\)$/);
            if (match) {
              matrix = match[1].split(',').map(Number);
            }
          } else {
            matrix = [
              parseFloat(canvas.style.width) / canvas.width,
              0,
              0,
              parseFloat(canvas.style.height) / canvas.height,
              0,
              0,
            ];
          }
          // Apply the transform to the export map context
          // @ts-ignore
          CanvasRenderingContext2D.prototype.setTransform.apply(mapContext, matrix);
          const backgroundColor = parentNode.style.backgroundColor;
          if (backgroundColor) {
            mapContext.fillStyle = backgroundColor;
            mapContext.fillRect(0, 0, canvas.width, canvas.height);
          }
          mapContext.drawImage(canvas, 0, 0);
        }
      });
      mapContext.globalAlpha = 1;
      mapContext.setTransform(1, 0, 0, 1, 0, 0);
      const link = document.createElement('a');
      link.href = mapCanvas.toDataURL();
      link.download = `${fileName}.png`;
      link.click();
    }
  }
}

export function isCoordinatesArray(input: unknown): input is Coordinate[] {
  return Array.isArray(input) && input.every((i) => Array.isArray(i));
}

export function getCoordinatesCenter(coordinates: Coordinate[]): Coordinate | null {
  if (!coordinates?.length) {
    return null;
  }
  const polygon = new Polygon([coordinates]);
  return getCenter(polygon.getExtent());
}

/**
 * Transforms a coordinate from locationEast/locationNorth to EPSG:3857 projection.
 * @param locationEast - longitude
 * @param locationNorth - latitude
 */
export function transformLonLat(locationEast: number, locationNorth: number): Coordinate {
  const [lat, lon] = fromLonLat([locationEast, locationNorth], EPSG_3857_PROJECTION);
  return [lat, lon];
}

export function getGeoMapData(
  geoLocationMarkers: MapMarker[],
  geoJsons: GeoGeometryGeoJson[] | undefined = undefined,
  configOverrides: Partial<GeoMapConfig> = {},
): {
  config: GeoMapConfig;
  mapMarkers: MapMarker[];
  geoJsons: GeoGeometryGeoJson[] | undefined;
} {
  const mapMarkers = geoLocationMarkers.map((item) => {
    const [lat, lon] = transformLonLat(item.lon, item.lat);
    const mapMarker: MapMarker = {
      lat,
      lon,
      text: item.text,
    };
    return mapMarker;
  });
  const startingPoint = mapMarkers.map((i) => {
    return [i.lat, i.lon];
  });
  return {
    config: {
      ...SMALL_GEO_MAP_CONFIG,
      startingPoint,
      ...configOverrides,
    },
    mapMarkers,
    geoJsons,
  };
}
